import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useRequirements } from '@/hooks/useRequirements'
import { useAIStore } from '@/stores/aiStore'
import type {
  RequirementNode,
  RequirementType,
  RequirementPriorityLevel,
  RequirementStatus,
} from '@ispot/shared'
import { DEFAULT_VISIBLE_REQUIREMENT_COLUMNS } from './constants'
import { AddRequirementModal } from './AddRequirementModal'
import { type GroupBy } from './CommandBar'
import { usePersonaLookup, matchPersonaLookup, buildPersonaIndexBySessionId, type PersonaLookupEntry } from './usePersonaLookup'
import { useRequirementsViewStore } from '@/stores/requirementsViewStore'
import { RequirementsTopbar } from './RequirementsTopbar'
import { DraftView } from './views/DraftView'
import { DocumentView } from './views/DocumentView'
import { OutlineView } from './views/OutlineView'
import { TableView } from './views/TableView'

interface RequirementsPageProps {
  accountId: string
  projectId: string
}

function findNode(tree: RequirementNode[], id: string): RequirementNode | null {
  for (const n of tree) {
    if (n.id === id) return n
    const inChildren = findNode(n.children, id)
    if (inChildren) return inChildren
  }
  return null
}

/**
 * When grouping by a non-hierarchy facet, flatten the tree and re-group rows
 * by the chosen key. Returned nodes are depth-0 leaf-equivalents — the table
 * renders them without further indentation.
 */
function groupFlatTree(tree: RequirementNode[], groupBy: GroupBy): RequirementNode[] {
  if (groupBy === 'hierarchy') return tree

  const flat: RequirementNode[] = []
  function visit(nodes: RequirementNode[]) {
    for (const n of nodes) {
      flat.push(n)
      if (n.children.length > 0) visit(n.children)
    }
  }
  visit(tree)

  const groups = new Map<string, RequirementNode[]>()
  for (const n of flat) {
    const key =
      groupBy === 'status'   ? n.status
    : groupBy === 'priority' ? (n.priority ?? 'unassigned')
    : (n.assigneeName ?? 'unassigned')
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push({ ...n, children: [], depth: 0 })
  }
  return Array.from(groups.values()).flat()
}

/** Walk the parent → children graph from a scope root and collect all descendants. */
function collectScopedRequirements<R extends { id: string; parentId: string | null }>(
  requirements: R[],
  scopeId: string,
): R[] {
  const childrenByParent = new Map<string, string[]>()
  for (const r of requirements) {
    const list = childrenByParent.get(r.parentId ?? '') ?? []
    list.push(r.id)
    childrenByParent.set(r.parentId ?? '', list)
  }
  const keep = new Set<string>([scopeId])
  const queue = [scopeId]
  while (queue.length > 0) {
    const id = queue.shift()!
    for (const kid of childrenByParent.get(id) ?? []) {
      if (!keep.has(kid)) {
        keep.add(kid)
        queue.push(kid)
      }
    }
  }
  return requirements.filter(r => keep.has(r.id))
}

export function RequirementsPage({ accountId, projectId }: RequirementsPageProps) {
  const apiKey = useAIStore(state => state.apiKey)
  const [showAddModal, setShowAddModal] = useState(false)
  const [addParentId, setAddParentId] = useState<string | undefined>(undefined)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [groupBy, setGroupBy] = useState<GroupBy>('hierarchy')

  // Filters: store is the single source of truth across all three views.
  const filterQuery = useRequirementsViewStore(s => s.searchQuery)
  const facetTypes = useRequirementsViewStore(s => s.facetTypes)
  const facetPriorities = useRequirementsViewStore(s => s.facetPriorities)
  const facetStatuses = useRequirementsViewStore(s => s.facetStatuses)
  const facetThemeIds = useRequirementsViewStore(s => s.facetThemeIds)

  const view = useRequirementsViewStore(s => s.view)
  const setProject = useRequirementsViewStore(s => s.setProject)
  useEffect(() => { setProject(projectId) }, [projectId, setProject])

  // Document view's "zoom to row" affordance writes a pendingDetailId, which
  // we consume here once the view actually flips so the drawer pops open.
  const pendingDetailId = useRequirementsViewStore(s => s.pendingDetailId)
  const setPendingDetailId = useRequirementsViewStore(s => s.setPendingDetailId)
  useEffect(() => {
    if (view === 'table' && pendingDetailId) {
      setDetailId(pendingDetailId)
      setPendingDetailId(null)
    }
  }, [view, pendingDetailId, setPendingDetailId])

  const {
    tree, requirements, themes, columnSettings, summary,
    selectedIds, expandedIds, isLoading, isGenerating, error,
    createRequirement, updateRequirement, deleteRequirement,
    generateFromResearch, bulkUpdateStatus, bulkDelete, bulkUpdatePriority,
    reorderRequirements, generateChildren,
    toggleExpanded, toggleSelected, deselectAll,
    updateColumnSettings, setRequirementThemes, setPrimaryPersona,
  } = useRequirements(accountId, projectId)

  const setFocusBlockId = useRequirementsViewStore(s => s.setFocusBlockId)

  // Inline create helper used by Tree + Document views.
  // - creates the requirement
  // - auto-links the group's persona as primary on stories
  // - arms focusBlockId so the new block's title auto-focuses for typing
  const handleCreateBlock = async (input: {
    type: 'story' | 'epic'
    name: string
    parentId?: string
    personaSessionId?: string
    section?: 'overview' | 'functional' | 'non-functional'
  }): Promise<{ id: string }> => {
    const req = await createRequirement({
      type: input.type,
      name: input.name,
      parentId: input.parentId,
    })
    // Stamp the document section on epics created from a section chapter.
    if (input.type === 'epic' && input.section) {
      try {
        await updateRequirement(req.id, { metadata: { section: input.section } })
      } catch { /* best effort */ }
    }
    if (input.type === 'story' && input.personaSessionId) {
      try { await setPrimaryPersona(req.id, input.personaSessionId) } catch { /* best effort */ }
      const personaName = personaIndex.get(input.personaSessionId)?.name
      if (personaName) {
        try {
          await updateRequirement(req.id, {
            metadata: { userStory: { role: personaName, want: '', benefit: '' } },
          })
        } catch { /* best effort */ }
      }
    }
    setFocusBlockId(req.id)
    return { id: req.id }
  }

  const handleReorder = async (parentId: string | null, orderedIds: string[]) => {
    await reorderRequirements(parentId, orderedIds)
  }

  const visibleColumns = columnSettings?.visibleColumns
    ?? (DEFAULT_VISIBLE_REQUIREMENT_COLUMNS as Record<string, boolean>)

  // Forward store facets to the table's filter shape. Multi-select still lives
  // in the store, but Table's matchesFilter takes single values for type/prio/status.
  const activeFilters = {
    type: facetTypes.size > 0 ? [...facetTypes][0] : undefined,
    priority: facetPriorities.size > 0 ? [...facetPriorities][0] : undefined,
    status: facetStatuses.size > 0 ? [...facetStatuses][0] : undefined,
    themeIds: facetThemeIds.size > 0 ? [...facetThemeIds] : undefined,
    personaIds: undefined as string[] | undefined,
  }

  const detailNode = detailId ? findNode(tree, detailId) : null
  const displayTree = useMemo(() => groupFlatTree(tree, groupBy), [tree, groupBy])

  // Tree-view scope.
  const scopeId = useRequirementsViewStore(s => s.scopeId)
  const scopedRequirements = useMemo(
    () => (scopeId ? collectScopedRequirements(requirements, scopeId) : requirements),
    [requirements, scopeId],
  )
  const scopedNode = scopeId ? findNode(tree, scopeId) : null

  // Persona lookup.
  const { data: personaLookup } = usePersonaLookup(accountId, projectId)
  const resolvePersona = useMemo(() => {
    return (name: string | undefined): PersonaLookupEntry | null =>
      matchPersonaLookup(name, personaLookup)
  }, [personaLookup])
  const personaIndex = useMemo(() => buildPersonaIndexBySessionId(personaLookup), [personaLookup])

  function handleAddChild(parentId: string) {
    setAddParentId(parentId)
    setShowAddModal(true)
  }

  async function handleCreate(data: {
    name: string
    description?: string
    type: RequirementType
    parentId?: string
    priority?: RequirementPriorityLevel
    status?: RequirementStatus
  }) {
    await createRequirement(data)
    setShowAddModal(false)
    setAddParentId(undefined)
  }

  async function handleGenerate() {
    await generateFromResearch(apiKey || '')
  }

  if (isLoading && requirements.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
      </div>
    )
  }

  // Reserved for a future "N new since last visit" badge; currently always 0.
  const newlyDetected = 0

  const errorBanner = error && (
    <div
      className="mx-6 mt-3 px-3 py-2 text-[13px] rounded-lg"
      style={{ background: 'rgba(231,76,60,0.08)', color: 'var(--color-error)' }}
    >
      {error}
    </div>
  )

  const sharedProps = {
    accountId, projectId,
    requirements, scopedRequirements, tree, scopedNode,
    themes, personaIndex,
    detailNode, detailId, setDetailId,
    updateRequirement, deleteRequirement, setRequirementThemes,
    createBlock: handleCreateBlock,
    reorderSiblings: handleReorder,
    resolvePersona,
    errorBanner,
  }

  return (
    <div className="flex flex-col h-full min-h-0" style={{ background: 'var(--color-warm-50)' }}>
      <RequirementsTopbar
        count={requirements.length}
        onAddClick={() => { setAddParentId(undefined); setShowAddModal(true) }}
      />

      <div className="flex flex-1 min-h-0">
        {view === 'outline'  && <OutlineView {...sharedProps} />}
        {view === 'document' && <DocumentView {...sharedProps} />}
        {view === 'draft'    && <DraftView {...sharedProps} />}
        {view === 'table'    && (
          <TableView
            {...sharedProps}
            displayTree={displayTree}
            visibleColumns={visibleColumns}
            updateColumnSettings={updateColumnSettings}
            expandedIds={expandedIds}
            selectedIds={selectedIds}
            toggleExpanded={toggleExpanded}
            toggleSelected={toggleSelected}
            deselectAll={deselectAll}
            filterQuery={filterQuery}
            activeFilters={activeFilters}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            onAddChild={handleAddChild}
            createRequirement={createRequirement}
            generateChildren={generateChildren}
            reorderRequirements={reorderRequirements}
            apiKey={apiKey || ''}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
            newlyDetected={newlyDetected}
            summary={summary}
            bulkUpdateStatus={bulkUpdateStatus}
            bulkUpdatePriority={bulkUpdatePriority}
            bulkDelete={bulkDelete}
          />
        )}
      </div>

      {showAddModal && (
        <AddRequirementModal
          parentId={addParentId}
          requirements={requirements}
          onSave={handleCreate}
          onClose={() => { setShowAddModal(false); setAddParentId(undefined) }}
        />
      )}
    </div>
  )
}
